import Styled from 'styled-components';
import {CSS_CONSTANTS} from '../../css_variables';

/*************************************************
 * HELPERS
 ************************************************/
export const TableColumnWidth = {
	EXTRA_SMALL: 'EXTRA_SMALL',
	SMALL: 'SMALL',
	MEDIUM_SMALL: 'MEDIUM_SMALL',
	MEDIUM: 'MEDIUM',
	LARGE: 'LARGE',
	EXTRA_LARGE: 'EXTRA_LARGE',
	SUPER_LARGE: 'SUPER_LARGE',
};

export const TableColumnAlignment = {
	LEFT: 'left',
	CENTER: 'center',
	RIGHT: 'right',
	SPACE_EVENLY: 'space-evenly',
};

const getExpansionWidth = level => {
	switch (level) {
		case 1:
			return 0;
		case 2:
			return 32;
		case 3:
			return 76;
		case 4:
			return 126;
		case 5:
			return 180;
		default:
			throw new Error(`level ${level} is not valid`);
	}
};

export const getWidth = (width, level, canExpand) => {
	let calculatedWidth;
	switch (width) {
		case TableColumnWidth.EXTRA_SMALL:
			calculatedWidth = 60;
			break;
		case TableColumnWidth.SMALL:
			calculatedWidth = 100;
			break;
		case TableColumnWidth.MEDIUM_SMALL:
			calculatedWidth = 130;
			break;
		case TableColumnWidth.MEDIUM:
			calculatedWidth = 160;
			break;
		case TableColumnWidth.LARGE:
			calculatedWidth = 220;
			break;
		case TableColumnWidth.EXTRA_LARGE:
			calculatedWidth = 280;
			break;
		case TableColumnWidth.SUPER_LARGE:
			// Width used for status description column in the project portfolio report table.
			// 656 pixels is equal to 82 characters.
			calculatedWidth = 656;
			break;
		default:
			calculatedWidth = 160;
	}

	if (level) {
		calculatedWidth -= getExpansionWidth(level) - 16;

		if (canExpand) {
			calculatedWidth -= 28;
		}
	}
	return calculatedWidth;
};

const alignmentToFlex = align => {
	switch (align) {
		case 'left':
			return 'flex-start';
		case 'right':
			return 'flex-end';
		case 'center':
			return 'center';
		case 'space-evenly':
			return 'space-evenly';
		default:
			throw new Error('Alignment prop invalid');
	}
};

const getLevelHeight = level => {
	switch (level) {
		case 1:
			return 48;
		case 2:
			return 40;
		case 3:
		case 4:
		case 5:
			return 32;
		default:
			return 0;
	}
};

const getBackgroundColor = color => {
	switch (color) {
		case 'GREY':
			return '#F4F5F8';
		default:
			break;
	}
};

/*************************************************
 * STYLES
 ************************************************/

export const TableContainer = Styled.div`
    display: flex;
    width: 100%;
	
`;
export const TableStyle = Styled.div`
    flex-grow: 1;
    background-color: #fff;
`;

/*-----------------------------------------------
    Header Styles
-----------------------------------------------*/
export const TableHeaderStyle = Styled.div`
	display: flex;
	position: sticky;
	top: ${({stickyOffset}) => (stickyOffset ? stickyOffset + 'px' : '0px')};
	border-bottom: 1px solid #e6e6e6;
	height: ${({useGrouping}) => (useGrouping ? `90px` : `53px`)};
	z-index: 10;
	background-color: ${({backgroundColor}) => backgroundColor || '#fff'};
`;

export const TitleStyle = Styled.div`
	
	display: flex;
	line-height: 12px;
	align-items: center;
	height: 22px;
	font-size: 9px;
	color: inherit;
	font-weight: ${({bold}) => (bold ? '700' : '400')};
	text-transform: uppercase;
	letter-spacing: 1px;
	padding: ${({usePadding}) => usePadding && '0 16px'};
	
`;

/*-----------------------------------------------
    Column Styles
-----------------------------------------------*/

export const BaseColumnStyle = Styled.div`
	display: flex;
	flex-direction: row;
	left: ${({level}) => !!level && '0px'};
	background-color: inherit;
	color: #2A2A37;
	align-items: center;
    min-width: ${({wrapped, width, level, canExpand}) => !wrapped && `${getWidth(width, level, canExpand, wrapped)}px`};
	width: ${({wrapped, width, level, canExpand}) => !wrapped && `${getWidth(width, level, canExpand, wrapped)}px`};
	max-width: ${({wrapped, width, level, canExpand}) => !wrapped && `${getWidth(width, level, canExpand, wrapped)}px`};
	flex-grow: ${({flex, wrapped}) => (wrapped ? 1 : flex)};
	text-align: ${({align}) => align};
	justify-content: ${({align}) => !!align && alignmentToFlex(align)};
	box-sizing: border-box;
    height: 100%;

`;

export const TableHeaderColumnStyle = Styled.div`
	align-items: center;
	background-color: inherit;
	box-sizing: border-box;
	color: ${({sorted}) => (sorted ? '#6e0fea' : '#7C7C9C')};
	cursor: ${({sortable}) => sortable && 'pointer'};
	display: flex;
	flex-direction: row;
	flex-grow: ${({flex}) => flex};
	font-weight: 500;
    height: 100%;
	justify-content: ${({align}) => !!align && alignmentToFlex(align)};
	left: ${({level}) => !!level && '0px'};
	min-width: ${({wrapped, width, level, canExpand}) => !wrapped && `${getWidth(width, level, canExpand)}px`};
	max-width: ${({wrapped, width, level, canExpand}) => !wrapped && `${getWidth(width, level, canExpand)}px`};
	padding-top: ${({groupingWhiteSpace}) => (groupingWhiteSpace ? '52px' : '16px')};
	padding-left: ${({level}) => !level && '16px'};
	padding-right: 16px;
	padding-bottom: 15px;
	text-align: ${({align}) => align};
	width: ${({wrapped, width, level, canExpand}) => (wrapped ? '100%' : `${getWidth(width, level, canExpand)}px`)};
	&:hover {
		color: ${({sortable, sorted}) => sortable && !sorted && '#2A2A37'};
		transition: color .1s ease-in;
	}
`;

export const TableColumnStyle = Styled(BaseColumnStyle)`
	white-space:  ${({wrapText}) => (wrapText ? 'auto' : 'nowrap')};
	text-overflow: ellipsis;
	overflow: hidden;
	padding-left: ${({usePadding, level}) => !level && usePadding && '16px'};
	padding-right: ${({usePadding}) => usePadding && '16px'};
	border-left: ${({borderLeft}) => borderLeft && '1px solid #e6e6e6'};
	border-right: ${({borderRight}) => borderRight && '1px solid #e6e6e6'};
	background: ${({background}) => background && getBackgroundColor(background)};
	font-weight: ${({bold}) => bold && '700'};
	color: ${({negative}) => negative && CSS_CONSTANTS.negative_value};
	opacity: ${({greyedOut}) => greyedOut && '0.6'};
`;

export const TableColumnsStyle = Styled.div`
	display: flex;
	position: ${({canExpand}) => canExpand && 'sticky'};
	height: ${({level, fixedHeight}) => `${fixedHeight || getLevelHeight(level)}px`};
	font-weight: ${({level}) => (level === 1 ? 500 : 'normal')};
	align-items: center;
	border-bottom: ${({level, isLastRow}) => (level < 3 || isLastRow) && '1px solid #e6e6e6'};
	cursor: ${({canExpand}) => (canExpand ? 'pointer' : 'normal')};

`;

export const FlexColumnStyle = Styled.div`
	display: flex;
	min-width: ${({width, level, canExpand}) => `${getWidth(width, level, canExpand)}px`};
	width: ${({width, level, canExpand}) => `${getWidth(width, level, canExpand)}px`};
	flex-grow: ${({flex}) => flex};
	background-color: inherit;
	color: #2A2A37;
	align-items: center;
	text-align: ${({align}) => align};
	justify-content: ${({align}) => !!align && alignmentToFlex(align)};
	box-sizing: border-box;
	height: 100%;
`;

export const FirstColumnStyle = Styled.div`
	display: flex;
	position: sticky;
	left: 0px;
    height: 100%;
	padding-left: 16px;	
	min-width: ${({width}) => `${getWidth(width, null, false)}px`};
	width: ${({width}) => `${getWidth(width, null, false)}px`};
	max-width: ${({width}) => `${getWidth(width, null, false)}px`};
	flex-direction: row;
	background-color: ${({isHeader}) => (isHeader ? '#F7F8FB' : '#fff')};
	color: #2A2A37;
	align-items: center;
	text-align: ${({align}) => align};
	justify-content: ${({align}) => !!align && alignmentToFlex(align)};
	height: 100%;
	padding-left: 16px;
	//z-index: ${({zIndex, level}) => zIndex || 5 - level};

`;

export const FirstColumnHeaderStyle = Styled.div`
	display: flex;
	position: sticky;
	left: 0;
	height: 100%;
	padding-left: 16px;
	padding-right: ${({width}) => (width === TableColumnWidth.EXTRA_SMALL ? '0px' : '16px')};
	z-index: 100;
	box-sizing: border-box;
	min-width: ${({width}) => `${getWidth(width, null, false)}px`};
	width: ${({width}) => `${getWidth(width, null, false)}px`};
	max-width: ${({width}) => `${getWidth(width, null, false)}px`};
	background-color: ${props => props.backgroundColor || '#fff'};
`;
export const FirstColumnFlexStyle = Styled(FlexColumnStyle)`
	position: sticky;
	left: 0px;
	height: 100%;
	padding-left: 16px;
	padding-right: ${({width}) => (width === TableColumnWidth.EXTRA_SMALL ? '0px' : '16px')};
	z-index: ${({zIndex, level}) => zIndex || 6 - level};
	background-color: ${({isHeader, backgroundColor}) => (backgroundColor ? backgroundColor : isHeader ? '#F7F8FB' : '#fff')};
`;

export const TableColumnExpansion = Styled.div`
	padding-left: ${({level}) => `${getExpansionWidth(level)}px`};
	padding-right: ${({canExpand}) => (canExpand ? 16 : 0)}px;
`;

export const GroupingStyle = Styled.div`
    background-color: inherit;
	border-left: 1px solid #e6e6e6;
	border-right: ${({lastGrouping}) => lastGrouping && '1px solid #e6e6e6'};
    height: 100%;
	${props => props.minWidth && 'min-width: ' + props.minWidth + ';'}
`;

export const HeaderGroupingStyle = Styled.div`
    background-color: '#fff';
	padding-top: 16px;
	border-left: 1px solid white;
	${props => props.minWidth && 'min-width: ' + props.minWidth + ';'}
`;

/*-----------------------------------------------
    Row Styles
-----------------------------------------------*/
export const TableRowStyle = Styled.div`
	position: relative;
	font-size: 13px;
	cursor: ${({clickHandled}) => clickHandled && 'pointer'};
	z-index: ${({level}) => !!level && 6 - level};
	background-color: ${({isHeader}) => (isHeader ? '#F7F8FB' : '#fff')};
	position: ${({canExpand}) => canExpand && 'sticky'};
	top: ${({level, canExpand, hasGroupings}) =>
		canExpand && `${(hasGroupings ? 73 + 16 : 37 + 16) + getLevelHeight(level - 1)}px`};
	&:hover {
		background-color: ${({isHeader}) => !isHeader && '#f8f3fe'};
		${FirstColumnStyle}, ${GroupingStyle}, ${FirstColumnFlexStyle} {
			background-color: ${({isHeader}) => !isHeader && '#f8f3fe'};
		}
		.row-hover {
			${props => props.hoverStyle}
		}
	}
`;

export const ButtonStyle = Styled.div`
	cursor: pointer;
	width: 94px;
	height: 32px;
	background: #FFFFFF;
	border: 1px solid #E6E6E6;
	box-sizing: border-box;
	font-weight: 500;
	font-size: 10px;
	color: #2A2A37;
	display: flex;
	z-index: 20;
	align-items: center;
	justify-content: center;
	text-transform: uppercase;
`;

export const TableClickableColumnStyle = Styled.div`
	cursor: pointer;
	display: inline-block;
	text-decoration: none;
	outline:none;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	&:hover{
		color:${props => props.theme.colors.hoverPurple};
	}
`;

export const TableItalicColumnStyle = Styled.div`
	font-style: italic;
	color:${props => props.theme.colors.mediumLightGray};
`;

export const CellStyle = Styled.div`
	display: inline-block;
	overflow: hidden;
	text-overflow: ellipsis;
	cursor: ${({clickHandled}) => clickHandled && 'pointer'};
	&:hover{
		color:${props => props.clickHandled && props.theme.colors.hoverPurple};
	}
`;
